import React, { useState, useCallback } from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { copyToClipboard } from './utils/clipboardUtils';

/**
 * Generic CopyButton Component
 * 
 * @param {Object} props
 * @param {string} props.text - Text to copy to clipboard
 * @param {Function} props.onCopy - Callback when copy succeeds (receives copied text)
 * @param {Function} props.onError - Callback when copy fails
 * @param {string} props.variant - Button variant: 'button' | 'icon' (default: 'button')
 * @param {string} props.label - Button label text (default: 'Copy')
 * @param {string} props.copiedLabel - Label when copied (default: 'Copied!')
 * @param {number} props.resetDelay - Delay in ms before resetting copied state (default: 2000)
 * @param {boolean} props.showTooltip - Show tooltip (default: true)
 * @param {string} props.tooltipTitle - Custom tooltip title
 * @param {boolean} props.disabled - Disable button
 * @param {Object} props.sx - Additional sx styles
 * @param {Object} props.buttonProps - Additional props to pass to Button/IconButton
 */
export const CopyButton = ({
    text,
    onCopy,
    onError,
    variant = 'button',
    label = 'Copy',
    copiedLabel = 'Copied!',
    resetDelay = 2000,
    showTooltip = true,
    tooltipTitle,
    disabled = false,
    sx,
    ...buttonProps
}) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(async () => {
        if (!text || disabled) return;

        try {
            const success = await copyToClipboard(text);
            if (success) {
                setCopied(true);
                if (onCopy) {
                    onCopy(text);
                }
                // Reset copied state after delay
                setTimeout(() => {
                    setCopied(false);
                }, resetDelay);
            } else {
                if (onError) {
                    onError(new Error('Failed to copy to clipboard'));
                }
            }
        } catch (err) {
            console.error('Failed to copy:', err);
            if (onError) {
                onError(err);
            }
        }
    }, [text, disabled, onCopy, onError, resetDelay]);

    const ButtonComponent = variant === 'icon' ? IconButton : Button;
    const buttonContent = variant === 'icon' ? (
        <ContentCopyIcon />
    ) : (
        <>
            <ContentCopyIcon sx={{ mr: 1 }} />
            {copied ? copiedLabel : label}
        </>
    );

    const tooltipText = tooltipTitle || (copied ? copiedLabel : label);
    const isDisabled = disabled || !text;

    const button = (
        <ButtonComponent
            onClick={handleCopy}
            disabled={isDisabled}
            {...buttonProps}
            sx={{
                ...(buttonProps.sx || {}),
                ...(sx || {}),
            }}
        >
            {buttonContent}
        </ButtonComponent>
    );

    if (showTooltip && !isDisabled) {
        return (
            <Tooltip title={tooltipText}>
                {button}
            </Tooltip>
        );
    }

    return button;
};

